import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  PartyPopper,
  Sparkles,
  Upload,
  User,
  Workflow,
} from "lucide-react";
import {
  getOnboardingState,
  saveOnboardingStep,
  completeOnboarding,
  setDefaultWorkspace,
} from "@/lib/onboarding.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { WorkspaceWizard } from "@/components/workspace-wizard";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/onboarding")({
  component: OnboardingPage,
});

const TOTAL_STEPS = 5;

const STEPS = [
  { n: 1, label: "Welcome", icon: User },
  { n: 2, label: "About you", icon: Sparkles },
  { n: 3, label: "Workspace", icon: Workflow },
  { n: 4, label: "Import", icon: Upload },
  { n: 5, label: "Done", icon: PartyPopper },
] as const;

const ROLES = [
  { value: "affiliate", label: "Affiliate / creator" },
  { value: "agency", label: "Agency / consultant" },
  { value: "coach", label: "Coach / course creator" },
  { value: "other", label: "Other" },
];

const GOALS = [
  { value: "track_commissions", label: "Track commissions across programs" },
  { value: "find_anomalies", label: "Catch refunds / chargebacks / leaks" },
  { value: "manage_team", label: "Manage a team of affiliates" },
  { value: "launch_bonuses", label: "Build bonuses & promote offers" },
];

const REFERRALS = [
  { value: "doug", label: "Doug Boughton" },
  { value: "referral", label: "Friend / colleague" },
  { value: "search", label: "Google search" },
  { value: "social", label: "Social media" },
  { value: "other", label: "Other" },
];

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const COMMON_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetchState = useServerFn(getOnboardingState);
  const saveStep = useServerFn(saveOnboardingStep);
  const setDefault = useServerFn(setDefaultWorkspace);
  const finish = useServerFn(completeOnboarding);

  const { data, isLoading } = useQuery({
    queryKey: ["onboarding-state"],
    queryFn: () => fetchState(),
  });

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [fullName, setFullName] = useState("");
  const [timezone, setTimezone] = useState<string>(detectTimezone());

  // Step 2
  const [role, setRole] = useState<string>("");
  const [goal, setGoal] = useState<string>("");
  const [referral, setReferral] = useState<string>("");
  const [referralOther, setReferralOther] = useState<string>("");

  // Step 3
  const [wizardOpen, setWizardOpen] = useState(false);

  // Already completed?, bounce to dashboard.
  useEffect(() => {
    if (!isLoading && data?.profile?.onboarding_completed_at) {
      navigate({ to: "/dashboard" });
    }
  }, [isLoading, data, navigate]);

  // Hydrate from server.
  useEffect(() => {
    if (!data) return;
    const p = data.profile;
    setFullName(p.full_name ?? "");
    if (p.timezone) setTimezone(p.timezone);
    if (p.role_self_reported) setRole(p.role_self_reported);
    if (p.primary_goal) setGoal(p.primary_goal);
    if (p.referral_source) setReferral(p.referral_source);
    if (p.referral_source_other) setReferralOther(p.referral_source_other);
    if (p.onboarding_step && p.onboarding_step > 0 && p.onboarding_step <= TOTAL_STEPS) {
      setStep(Math.min(p.onboarding_step + 1, TOTAL_STEPS));
    }
  }, [data]);

  // When the wizard closes after creating a workspace, set it as the default.
  const accountId = data?.account_id ?? null;
  const hadWorkspace = useMemo(() => (data?.workspace_count ?? 0) > 0, [data]);

  const handleWizardChange = async (open: boolean) => {
    setWizardOpen(open);
    if (!open) {
      // Re-fetch state to pick up any newly created workspace.
      const fresh = await qc.fetchQuery({
        queryKey: ["onboarding-state"],
        queryFn: () => fetchState(),
      });
      const newWs = fresh?.workspace_count ?? 0;
      const accId = fresh?.account_id;
      if (newWs > (data?.workspace_count ?? 0) && accId) {
        // Get the most recent workspace for this account and pin it as default.
        try {
          const { supabase } = await import("@/integrations/supabase/client");
          const { data: ws } = await supabase
            .from("workspaces")
            .select("id, created_at")
            .eq("account_id", accId)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (ws) {
            await setDefault({ data: { account_id: accId, workspace_id: ws.id } });
          }
        } catch (err) {
          // Non-fatal, user can pick a default later.
          console.warn("Could not set default workspace", err);
        }
      }
    }
  };

  const goNext = async (persist?: () => Promise<void>) => {
    setSubmitting(true);
    try {
      if (persist) await persist();
      const next = step + 1;
      await saveStep({ data: { step } });
      setStep(next);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleStep1 = () =>
    goNext(async () => {
      if (!fullName.trim()) throw new Error("Please enter your name");
      await saveStep({ data: { step: 1, full_name: fullName.trim(), timezone } });
    });

  const handleStep2 = () =>
    goNext(async () => {
      if (!role) throw new Error("Please pick one");
      if (!goal) throw new Error("Please pick a primary goal");
      await saveStep({
        data: {
          step: 2,
          role_self_reported: role,
          primary_goal: goal,
          referral_source: referral || null,
          referral_source_other: referral === "other" ? referralOther.trim() || null : null,
        },
      });
    });

  const handleStep3Skip = () => goNext();
  const handleStep3Continue = () => goNext();
  const handleStep4Skip = () => goNext();
  const handleStep4Continue = () => goNext();

  const handleFinish = async (withTour: boolean) => {
    setSubmitting(true);
    try {
      await finish({});
      navigate({ to: "/dashboard", search: withTour ? ({ tour: 1 } as never) : ({} as never) });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't finish");
      setSubmitting(false);
    }
  };

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl py-10">
      {/* Progress */}
      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            Step {step} of {TOTAL_STEPS}
          </span>
          <span>{STEPS[step - 1].label}</span>
        </div>
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors",
                i < step ? "bg-primary" : "bg-muted",
              )}
            />
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-8 shadow-sm">
        {step === 1 && (
          <div className="space-y-6">
            <Header
              icon={<User className="h-5 w-5" />}
              title={`Welcome${fullName ? `, ${fullName.split(" ")[0]}` : ""}`}
              subtitle="Let's set up your account in under two minutes."
            />
            <div className="space-y-2">
              <Label htmlFor="ob-name">Your name</Label>
              <Input
                id="ob-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ob-tz">Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id="ob-tz">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([timezone, ...COMMON_TIMEZONES])].map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Used for digest delivery and date formatting.
              </p>
            </div>
            <Footer
              onNext={handleStep1}
              nextLabel="Continue"
              nextDisabled={submitting || !fullName.trim()}
              loading={submitting}
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <Header
              icon={<Sparkles className="h-5 w-5" />}
              title="Tell us about you"
              subtitle="Helps us tailor your dashboard. We never share this."
            />
            <div className="space-y-2">
              <Label>You're primarily a…</Label>
              <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-2">
                {ROLES.map((r) => (
                  <Label
                    key={r.value}
                    htmlFor={`role-${r.value}`}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm transition",
                      role === r.value
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:border-primary/50",
                    )}
                  >
                    <RadioGroupItem id={`role-${r.value}`} value={r.value} />
                    {r.label}
                  </Label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label>What do you want to do first?</Label>
              <RadioGroup value={goal} onValueChange={setGoal} className="space-y-2">
                {GOALS.map((g) => (
                  <Label
                    key={g.value}
                    htmlFor={`goal-${g.value}`}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm transition",
                      goal === g.value
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:border-primary/50",
                    )}
                  >
                    <RadioGroupItem id={`goal-${g.value}`} value={g.value} />
                    {g.label}
                  </Label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ob-ref">How did you hear about us?</Label>
              <Select value={referral} onValueChange={setReferral}>
                <SelectTrigger id="ob-ref">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {REFERRALS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {referral === "other" && (
                <Input
                  className="mt-2"
                  value={referralOther}
                  onChange={(e) => setReferralOther(e.target.value)}
                  placeholder="Tell us more"
                />
              )}
            </div>
            <Footer
              onBack={goBack}
              onNext={handleStep2}
              nextDisabled={submitting || !role || !goal}
              loading={submitting}
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <Header
              icon={<Workflow className="h-5 w-5" />}
              title="Create your first workspace"
              subtitle="A workspace tracks one affiliate program (e.g. Prime Mover, ClickFunnels, Skool)."
            />
            {hadWorkspace || (data?.workspace_count ?? 0) > 0 ? (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm">
                <div className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-400">
                  <Check className="h-4 w-4" />
                  Workspace created
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  You can create more workspaces anytime from the sidebar.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
                <Workflow className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 text-sm font-medium">No workspace yet</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Pick a program template, upload a CSV, or start from scratch.
                </p>
                <Button className="mt-4" onClick={() => setWizardOpen(true)} disabled={!accountId}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Launch workspace wizard
                </Button>
              </div>
            )}
            <Footer
              onBack={goBack}
              onNext={(data?.workspace_count ?? 0) > 0 ? handleStep3Continue : handleStep3Skip}
              nextLabel={(data?.workspace_count ?? 0) > 0 ? "Continue" : "Skip for now"}
              loading={submitting}
              nextDisabled={submitting}
            />
            {accountId && (
              <WorkspaceWizard
                open={wizardOpen}
                onOpenChange={handleWizardChange}
                accountId={accountId}
              />
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <Header
              icon={<Upload className="h-5 w-5" />}
              title="Import your commissions"
              subtitle="Upload a CSV from any platform, we'll auto-map columns."
            />
            <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
              <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">
                {(data?.workspace_count ?? 0) > 0
                  ? "Head to Imports to upload your first file"
                  : "Create a workspace first (Step 3) to enable imports"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                You can also do this later, the dashboard works fine empty.
              </p>
              {(data?.workspace_count ?? 0) > 0 && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate({ to: "/imports" })}
                >
                  Open Imports
                </Button>
              )}
            </div>
            <Footer
              onBack={goBack}
              onNext={handleStep4Continue}
              nextLabel="Skip for now"
              loading={submitting}
              nextDisabled={submitting}
            />
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <PartyPopper className="h-7 w-7" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">You're all set</h2>
              <p className="text-sm text-muted-foreground">
                {(data?.workspace_count ?? 0) > 0
                  ? `${data?.workspace_count} workspace ready to go.`
                  : "Your account is ready. Create a workspace when you're set."}
              </p>
            </div>
            <div className="grid gap-2 pt-2 sm:grid-cols-2">
              <Button
                onClick={() => handleFinish(true)}
                disabled={submitting}
                className="w-full"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                Take the 60-second tour
              </Button>
              <Button
                variant="outline"
                onClick={() => handleFinish(false)}
                disabled={submitting}
                className="w-full"
              >
                Just take me to the dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Header({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function Footer({
  onBack,
  onNext,
  nextLabel = "Continue",
  nextDisabled,
  loading,
}: {
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;
}) {
  return (
    <div className="flex items-center justify-between pt-2">
      {onBack ? (
        <Button variant="ghost" onClick={onBack} disabled={loading} size="sm">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      ) : (
        <span />
      )}
      <Button onClick={onNext} disabled={nextDisabled}>
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <>
            {nextLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
    </div>
  );
}
